import { useCallback, useMemo } from 'react';
import { useAuthContext, useTenantContext } from './contexts.tsx';
import {
  createAuthenticatedFetch,
  buildFullUrl,
  DEFAULT_TIMEOUT_MS,
  DEFAULT_TRANSFER_TIMEOUT_MS,
} from './apiFetchAuthWithRefresh.ts';

/**
 * Shape of an authenticated request. JSON body is the default; for FormData
 * uploads or other non-JSON payloads, set `body` directly and (optionally)
 * disable the JSON content-type with `json: false`.
 */
export interface ApiRequestInit
  extends Omit<RequestInit, 'body' | 'method'> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' | string;
  /** Plain JSON body — auto-stringified, content-type set to application/json. */
  json?: unknown;
  /** Raw body — passed through untouched. Use for FormData / Blob / streaming uploads. */
  body?: BodyInit | null;
  /**
   * Per-request client timeout in milliseconds. A hung gateway otherwise
   * leaves the fetch pending forever. Default 30 s; uploads/downloads use a
   * larger default. Composed with any caller-supplied `signal`.
   */
  timeoutMs?: number;
}

export interface ApiClient {
  /** Fetch JSON from the gateway with auth + tenant headers attached. */
  request<T = unknown>(endpoint: string, init?: ApiRequestInit): Promise<T>;
  /** Convenience for GET. */
  get<T = unknown>(endpoint: string, init?: Omit<ApiRequestInit, 'method' | 'json'>): Promise<T>;
  /** Convenience for POST with a JSON body. */
  post<T = unknown, B = unknown>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>): Promise<T>;
  /** Convenience for PUT with a JSON body. */
  put<T = unknown, B = unknown>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>): Promise<T>;
  /** Convenience for PATCH with a JSON body. */
  patch<T = unknown, B = unknown>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>): Promise<T>;
  /** Convenience for DELETE. */
  del<T = unknown>(endpoint: string, init?: Omit<ApiRequestInit, 'method' | 'json'>): Promise<T>;
  /** Download a binary blob (PDF / CSV / image). */
  blob(endpoint: string, init?: ApiRequestInit): Promise<Blob>;
  /** Upload FormData; pass any extra method/headers in init. Returns parsed JSON. */
  upload<T = unknown>(endpoint: string, form: FormData, init?: Omit<ApiRequestInit, 'json' | 'body' | 'method'> & { method?: 'POST' | 'PUT' }): Promise<T>;
  /**
   * Issue an authenticated request and return the raw {@link Response} WITHOUT
   * decoding the body. Use for streaming endpoints (NDJSON / SSE) where the
   * caller reads `response.body` incrementally rather than awaiting a single
   * JSON payload — e.g. the Copilot `/ask-stream` token stream. Auth + tenant
   * headers and the silent-refresh path are attached exactly as for `request`;
   * a non-ok status is thrown (with `statusCode`) so the caller can branch.
   */
  stream(endpoint: string, init?: ApiRequestInit): Promise<Response>;
  /** Build the full URL — useful for `<a href>` downloads or window.open(). */
  buildUrl(endpoint: string): string;
}

/**
 * Tyndo API client hook — imperative complement to `useApiQuery`/`useApiMutation`.
 *
 * Use this when:
 *   - you need to download a blob (PDF / CSV / image),
 *   - upload FormData,
 *   - chain several authenticated calls in one user gesture,
 *   - call from outside a React render (e.g. inside an event handler that
 *     spawns a complex flow).
 *
 * The returned client is stable across re-renders so it's safe to put in
 * dependency arrays.
 */
export function useApiClient(): ApiClient {
  const { token } = useAuthContext();
  const { tenantId } = useTenantContext();

  // Single network + auth + silent-refresh path, shared verbatim with the
  // hook layer (A5-441). This client keeps only its own response decoding:
  // Error instances (not ApiError objects) and blob bodies.
  const authedFetch = useMemo(
    () => createAuthenticatedFetch(token, tenantId),
    [token, tenantId]
  );

  const request = useCallback(
    async <T,>(endpoint: string, init: ApiRequestInit = {}): Promise<T> => {
      const { timeoutMs, ...rest } = init;
      const response = await authedFetch(endpoint, {
        ...rest,
        timeoutMs: timeoutMs ?? DEFAULT_TIMEOUT_MS,
      });

      if (response.status === 401) {
        const err = new Error('Unauthorized') as Error & { statusCode?: number };
        err.statusCode = 401;
        throw err;
      }
      if (response.status === 403) {
        const err = new Error('Forbidden') as Error & { statusCode?: number };
        err.statusCode = 403;
        throw err;
      }

      if (!response.ok) {
        let message = response.statusText;
        try {
          const data = await response.json();
          message = data?.message ?? message;
        } catch {
          /* ignore */
        }
        const err = new Error(message) as Error & { statusCode?: number };
        err.statusCode = response.status;
        throw err;
      }

      const ct = response.headers.get('content-type') ?? '';
      if (ct.includes('application/json')) {
        return (await response.json()) as T;
      }
      // Empty 204, or non-JSON success — return undefined typed as T.
      return undefined as T;
    },
    [authedFetch]
  );

  const blobRequest = useCallback(
    async (endpoint: string, init: ApiRequestInit = {}): Promise<Blob> => {
      const { timeoutMs, ...rest } = init;
      const response = await authedFetch(endpoint, {
        ...rest,
        method: init.method ?? 'GET',
        timeoutMs: timeoutMs ?? DEFAULT_TRANSFER_TIMEOUT_MS,
      });

      if (response.status === 401) {
        const err = new Error('Unauthorized') as Error & { statusCode?: number };
        err.statusCode = 401;
        throw err;
      }
      if (response.status === 403) {
        const err = new Error('Forbidden') as Error & { statusCode?: number };
        err.statusCode = 403;
        throw err;
      }
      if (!response.ok) {
        const err = new Error(response.statusText) as Error & { statusCode?: number };
        err.statusCode = response.status;
        throw err;
      }
      return response.blob();
    },
    [authedFetch]
  );

  const streamRequest = useCallback(
    async (endpoint: string, init: ApiRequestInit = {}): Promise<Response> => {
      const { timeoutMs, json, ...rest } = init;
      // A streaming read holds the connection open for the whole token stream,
      // so default to the larger transfer timeout (the caller may pass a longer
      // per-operation timeout, or compose its own AbortSignal).
      const headers = new Headers(rest.headers as HeadersInit | undefined);
      let body = rest.body;
      if (json !== undefined) {
        headers.set('content-type', 'application/json');
        body = JSON.stringify(json);
      }
      const response = await authedFetch(endpoint, {
        ...rest,
        method: init.method ?? 'POST',
        headers,
        body,
        timeoutMs: timeoutMs ?? DEFAULT_TRANSFER_TIMEOUT_MS,
      });

      if (response.status === 401 || response.status === 403) {
        const err = new Error(
          response.status === 401 ? 'Unauthorized' : 'Forbidden',
        ) as Error & { statusCode?: number };
        err.statusCode = response.status;
        throw err;
      }
      if (!response.ok) {
        let message = response.statusText;
        try {
          const data = await response.json();
          message = data?.error ?? data?.message ?? message;
        } catch {
          /* non-JSON error body — keep statusText */
        }
        const err = new Error(message) as Error & { statusCode?: number };
        err.statusCode = response.status;
        throw err;
      }
      return response;
    },
    [authedFetch],
  );

  const buildUrl = useCallback((endpoint: string) => buildFullUrl(endpoint), []);

  return useMemo<ApiClient>(
    () => ({
      request,
      get: <T,>(endpoint: string, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
        request<T>(endpoint, { ...init, method: 'GET' }),
      post: <T, B>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
        request<T>(endpoint, { ...init, method: 'POST', json: body }),
      put: <T, B>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
        request<T>(endpoint, { ...init, method: 'PUT', json: body }),
      patch: <T, B>(endpoint: string, body?: B, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
        request<T>(endpoint, { ...init, method: 'PATCH', json: body }),
      del: <T,>(endpoint: string, init?: Omit<ApiRequestInit, 'method' | 'json'>) =>
        request<T>(endpoint, { ...init, method: 'DELETE' }),
      blob: blobRequest,
      stream: streamRequest,
      upload: <T,>(
        endpoint: string,
        form: FormData,
        init?: Omit<ApiRequestInit, 'json' | 'body' | 'method'> & { method?: 'POST' | 'PUT' }
      ) =>
        request<T>(endpoint, {
          ...init,
          method: init?.method ?? 'POST',
          body: form,
          // Uploads get the larger transfer timeout unless the caller overrides.
          timeoutMs: init?.timeoutMs ?? DEFAULT_TRANSFER_TIMEOUT_MS,
        }),
      buildUrl,
    }),
    [request, blobRequest, streamRequest, buildUrl]
  );
}
